import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { attachUserAssessmentToAccount } from 'src/models/assessment/assessment';
import { ClientFilter } from 'src/shared/Filters/ClientFilter';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

interface AttachToAccountDialogProps {
  open: boolean;
  onClose: () => void;
  userAssessmentId: Identifier | null;
}

export const AttachToAccountDialog: React.FC<AttachToAccountDialogProps> = ({
  open,
  onClose,
  userAssessmentId,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessments',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<Identifier | undefined>(
    undefined
  );

  const attachMutation = useMutation({
    mutationFn: attachUserAssessmentToAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadAssessments'] });
      toast.success(t('attachSuccess'));
      handleClose();
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  const handleClose = () => {
    setSelectedUserId(undefined);
    onClose();
  };

  const handleConfirm = () => {
    if (!userAssessmentId || !selectedUserId) return;

    attachMutation.mutate({
      userAssessmentId,
      userId: selectedUserId,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('attachToAccount')}
      maxWidth="sm"
      actions={[
        { label: tCommon('buttons.cancel'), onClick: handleClose },
        {
          label: tCommon('buttons.confirm'),
          onClick: handleConfirm,
          variant: 'prominent',
          disabled: !selectedUserId,
          loading: attachMutation.isPending,
        },
      ]}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('selectAccount')}
        </Typography>
        <ClientFilter
          label={t('selectClient')}
          value={selectedUserId}
          setValue={setSelectedUserId}
          useUserId
        />
      </Stack>
    </Dialog>
  );
};
